import { useEffect, useRef, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import { Button, EButtonType } from '../../components/Button';
import './companyProfile.scss';
import { CompanyProfile as CompanyProfileType } from '../../types/CompanyProfile.type';

interface Props {
    actions: any;
    adminActions: any;
    companyProfile: CompanyProfileType | null;
    buttonLoader: boolean;
}

const CompanyProfile = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [fiscalNumber, setFiscalNumber] = useState('');
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fillFromProfile = (profile: CompanyProfileType) => {
        setName(profile.name || '');
        setAddress(profile.address || '');
        setCity(profile.city || '');
        setPhone(profile.phone || '');
        setEmail(profile.email || '');
        setFiscalNumber(profile.fiscalNumber || '');
        setLogoPreview(profile.logo || null);
    };

    useEffect(() => { props.actions.fetchCompanyProfile(); }, []);

    useEffect(() => {
        if (props.companyProfile) fillFromProfile(props.companyProfile);
    }, [props.companyProfile]);

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setLogoFile(file);
        if (file) setLogoPreview(URL.createObjectURL(file));
    };

    const saveProfile = () => {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('address', address);
        formData.append('city', city);
        formData.append('phone', phone);
        formData.append('email', email);
        formData.append('fiscalNumber', fiscalNumber);
        if (logoFile) formData.append('logo', logoFile);
        props.adminActions.updateCompanyProfile(formData);
    };

    return (
        <div className="admin-page">
            <div className="cp-page">
                {/* ─── Header ─── */}
                <div className="cp-page-header">
                    <div>
                        <h1 className="cp-page-title"><FormattedMessage id="admin.companyProfile.pageTitle" defaultMessage="Profili i kompanisë" /></h1>
                        <p className="cp-page-subtitle"><FormattedMessage id="admin.companyProfile.pageSubtitle" defaultMessage="Menaxhoni të dhënat dhe logon e kompanisë" /></p>
                    </div>
                    <Button
                        isLoading={props.buttonLoader}
                        isDisabled={props.buttonLoader}
                        buttonType={EButtonType.PurpleButton}
                        onClick={saveProfile}>
                        <FormattedMessage id="admin.companyProfile.btnSave" defaultMessage="Ruaj ndryshimet" />
                    </Button>
                </div>

                {/* ─── Two Column Layout ─── */}
                <div className="cp-grid">

                    {/* ─── LEFT COLUMN ─── */}
                    <div className="cp-col">

                        {/* Card: Company Info */}
                        <div className="cp-card">
                            <div className="cp-card-header">
                                <h2><FormattedMessage id="admin.companyProfile.cardCompanyData" defaultMessage="Të dhënat e kompanisë" /></h2>
                            </div>
                            <div className="cp-card-body">
                                <div className="cp-field">
                                    <label><FormattedMessage id="admin.companyProfile.fieldName" defaultMessage="Emri i kompanisë" /> *</label>
                                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={fm('admin.companyProfile.fieldNamePlaceholder', 'Shkruani emrin e kompanisë')} />
                                </div>
                                <div className="cp-field">
                                    <label><FormattedMessage id="admin.companyProfile.fieldFiscal" defaultMessage="Numri fiskal" /></label>
                                    <input type="text" value={fiscalNumber} onChange={(e) => setFiscalNumber(e.target.value)} placeholder={fm('admin.companyProfile.fieldFiscalPlaceholder', 'P.sh: 123456789')} />
                                </div>
                                <div className="cp-field-row">
                                    <div className="cp-field">
                                        <label><FormattedMessage id="admin.companyProfile.fieldPhone" defaultMessage="Telefoni" /></label>
                                        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={fm('admin.companyProfile.fieldPhonePlaceholder', '+383 44 000 000')} />
                                    </div>
                                    <div className="cp-field">
                                        <label><FormattedMessage id="admin.companyProfile.fieldEmail" defaultMessage="Email" /></label>
                                        <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={fm('admin.companyProfile.fieldEmailPlaceholder', 'info@kompania.com')} />
                                    </div>
                                </div>
                                <div className="cp-field-row">
                                    <div className="cp-field">
                                        <label><FormattedMessage id="admin.companyProfile.fieldAddress" defaultMessage="Adresa" /></label>
                                        <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={fm('admin.companyProfile.fieldAddressPlaceholder', 'Rruga, Nr.')} />
                                    </div>
                                    <div className="cp-field">
                                        <label><FormattedMessage id="admin.companyProfile.fieldCity" defaultMessage="Qyteti" /></label>
                                        <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder={fm('admin.companyProfile.fieldCityPlaceholder', 'Prishtinë')} />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* ─── RIGHT COLUMN ─── */}
                    <div className="cp-col">

                        {/* Card: Logo */}
                        <div className="cp-card">
                            <div className="cp-card-header">
                                <h2><FormattedMessage id="admin.companyProfile.cardLogo" defaultMessage="Logo e kompanisë" /></h2>
                                {logoPreview && (
                                    <button type="button" className="cp-card-header-action" onClick={() => fileInputRef.current?.click()}>
                                        <FormattedMessage id="common.action.edit" defaultMessage="Ndrysho" />
                                    </button>
                                )}
                            </div>
                            <div className="cp-card-body">
                                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} className="cp-hidden-input" />
                                <div className="cp-logo-zone" onClick={() => fileInputRef.current?.click()}>
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo" className="cp-logo-img" />
                                    ) : (
                                        <div className="cp-logo-placeholder">
                                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#c0c0c0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                <polyline points="17 8 12 3 7 8"/>
                                                <line x1="12" y1="3" x2="12" y2="15"/>
                                            </svg>
                                            <span><FormattedMessage id="admin.companyProfile.logoCTA" defaultMessage="Klikoni për të ngarkuar logon" /></span>
                                            <span className="cp-logo-hint"><FormattedMessage id="admin.companyProfile.logoHint" defaultMessage="PNG, JPG ose WebP, max 2MB" /></span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ─── Bottom Save (mobile) ─── */}
                <div className="cp-bottom-save">
                    <Button
                        isLoading={props.buttonLoader}
                        isDisabled={props.buttonLoader}
                        buttonType={EButtonType.PurpleButton}
                        onClick={saveProfile}>
                        <FormattedMessage id="admin.companyProfile.btnSave" defaultMessage="Ruaj ndryshimet" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(CompanyProfile as any)));
